/**
 * The Figma-styled text field, copied from pdf-to-figma so the family shares one.
 *
 * The licence key box in the screenshots is this. It carries its own label, helper text and
 * error line because those three always travel with a field, and a caller assembling them by
 * hand gets the spacing subtly wrong each time.
 *
 * `helperText` is hidden while there is an `error`: two lines of small print under one box,
 * one explaining how it should look and one saying it is wrong, is a field arguing with itself.
 */

import { Field, Input as ChakraInput } from '@chakra-ui/react';
import type { ChangeEvent, HTMLInputTypeAttribute, KeyboardEvent } from 'react';

export interface InputProps {
  value?: string;
  onChange?: ((value: string) => void) | undefined;
  placeholder?: string | undefined;
  label?: string | undefined;
  /** Shown in place of `helperText`, and turns the border red. */
  error?: string | undefined;
  helperText?: string | undefined;
  size?: 'xs' | 'sm' | 'md';
  /*
   * The HTML union, not `string`. A plain string compiles here and fails in whichever app has
   * the stricter config — office-to-design is the one that found it — and the union is also
   * the thing that tells a caller `'passwrod'` is not a type.
   */
  type?: HTMLInputTypeAttribute | undefined;
  autoFocus?: boolean | undefined;
  /**
   * Key handling on the field itself — Enter to submit, in practice.
   *
   * `onChange` hands over a VALUE rather than an event, which is the right shape for a
   * controlled field and the wrong one for "was that Enter?". This is the escape hatch for
   * the second question, and the only one: the element is not otherwise exposed.
   */
  onKeyDown?: ((event: KeyboardEvent<HTMLInputElement>) => void) | undefined;
  disabled?: boolean | undefined;
}

export function Input({
  value,
  onChange,
  placeholder,
  label,
  error,
  helperText,
  size = 'sm',
  type,
  autoFocus,
  onKeyDown,
  disabled,
}: InputProps) {
  return (
    <Field.Root invalid={!!error} disabled={disabled}>
      {label && (
        <Field.Label fontSize="xs" fontWeight="500" color="figma.textSecondary" mb="1">
          {label}
        </Field.Label>
      )}
      <ChakraInput
        size={size}
        value={value}
        type={type}
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange?.(event.currentTarget.value)}
        bg="figma.panel"
        color="figma.text"
        borderColor="figma.border"
        borderRadius="md"
        fontSize="xs"
        _placeholder={{ color: 'figma.textMuted' }}
        _hover={{ borderColor: 'figma.borderLight' }}
        _focus={{
          borderColor: 'figma.accent',
          boxShadow: '0 0 0 1px var(--chakra-colors-figma-accent)',
          outline: 'none',
        }}
        _invalid={{
          borderColor: 'figma.error',
          boxShadow: '0 0 0 1px var(--chakra-colors-figma-error)',
        }}
      />
      {helperText && !error && (
        <Field.HelperText fontSize="2xs" color="figma.textMuted" mt="1">
          {helperText}
        </Field.HelperText>
      )}
      {error && (
        <Field.ErrorText fontSize="2xs" color="figma.error" mt="1">
          {error}
        </Field.ErrorText>
      )}
    </Field.Root>
  );
}
